import {
  Button,
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  ContactType,
  NotificationType,
  TransactionSide,
  TransactionStageType,
} from '@client/graphql/__generated__/types';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useNotificationTemplate } from '../../hooks/useNotificationTemplate';
import { useNotificationTemplateMutations } from '../../hooks/useNotificationTemplateMutations';
import { DataToShareSelect } from './DataToShareSelect';
import { DelaySelect } from './DelaySelect';
import { EventTypeSelect } from './EventTypeSelect';
import { MessageEditor } from './MessageEditor';
import { NotificationTypeToggle } from './NotificationTypeToggle';
import { RecipientsSelect } from './RecipientsSelect';
import { RelationshipToggle } from './RelationshipToggle';

const isValidEventType = (value: string): value is TransactionStageType =>
  Object.values(TransactionStageType).includes(value as TransactionStageType);

const isValidTransactionSide = (
  value: string | null
): value is TransactionSide =>
  value !== null &&
  Object.values(TransactionSide).includes(value as TransactionSide);

export const NotificationTemplateModal = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { partnerId, notificationTemplateId } = useParams<{
    partnerId: string;
    notificationTemplateId: string;
  }>();

  const isEditMode = !!notificationTemplateId;

  // Get initial eventType from search params
  const initialEventType = (() => {
    const param = searchParams.get('eventType');
    if (param && isValidEventType(param)) {
      return param;
    }

    return TransactionStageType.SIGNED_AGENCY_AGREEMENT;
  })();

  const { template, loading: loadingTemplate } = useNotificationTemplate(
    notificationTemplateId
  );
  const {
    createTemplate,
    updateTemplate,
    deleteTemplate,
    loading: mutationLoading,
    deleting,
  } = useNotificationTemplateMutations();

  // Form state
  const [eventType, setEventType] =
    useState<TransactionStageType>(initialEventType);
  const [notificationType, setNotificationType] = useState<NotificationType>(
    NotificationType.TEXT
  );
  const [recipients, setRecipients] = useState<ContactType[]>([
    ContactType.AGENT,
  ]);
  const [hasAssignedPartner, setHasAssignedPartner] = useState<boolean | null>(
    null
  );
  const [delayMinutes, setDelayMinutes] = useState<number>(0);
  const [dataToShare, setDataToShare] = useState<string[]>([]);
  const [messageContent, setMessageContent] = useState<string>('');

  // Get side from URL params or default to BUY
  const sideParam = searchParams.get('side');
  const side =
    template?.side ??
    (isValidTransactionSide(sideParam) ? sideParam : TransactionSide.BUY);

  // Populate form when editing
  useEffect(() => {
    if (template) {
      setEventType(template.eventType);
      setNotificationType(template.notificationType);
      setRecipients(template.recipients);
      setHasAssignedPartner(template.hasAssignedPartner ?? null);
      setDelayMinutes(template.delayMinutes ?? 0);
      setDataToShare(template.dataToShare ?? []);
      setMessageContent(template.messageContent);
    }
  }, [template]);

  const handleClose = () => {
    navigate(`/organizations/notifications/partners/${partnerId}`, {
      replace: true,
    });
  };

  const handleSave = () => {
    if (!partnerId) return;

    // Derive title from message content (first 50 chars or first line)
    const title = messageContent.split('\n')[0].substring(0, 50) || 'Untitled';

    if (isEditMode && notificationTemplateId) {
      void updateTemplate({
        id: notificationTemplateId,
        title,
        notificationType,
        recipients,
        hasAssignedPartner: hasAssignedPartner ?? undefined,
        delayMinutes,
        dataToShare,
        messageContent,
      }).then(handleClose);
    } else {
      void createTemplate({
        partnerId,
        title,
        eventType,
        side,
        notificationType,
        recipients,
        hasAssignedPartner: hasAssignedPartner ?? undefined,
        delayMinutes,
        dataToShare,
        messageContent,
      }).then(handleClose);
    }
  };

  const handleDelete = () => {
    if (!notificationTemplateId) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Are you sure you want to delete this notification template?'
    );

    if (confirmed) {
      void deleteTemplate(notificationTemplateId).then(handleClose);
    }
  };

  const isLoading = mutationLoading || loadingTemplate;
  const isValid = messageContent.trim().length > 0 && recipients.length > 0;

  return (
    <Modal isOpen size="xl" onClose={handleClose}>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent
        bg="indigo.700"
        borderColor="whiteAlpha.100"
        borderRadius="2xl"
        borderWidth="1px"
        maxWidth="540px"
      >
        <ModalHeader
          borderBottom="1px solid"
          borderColor="whiteAlpha.50"
          pb={4}
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Text
              color="white"
              fontSize="xs"
              letterSpacing="wider"
              textTransform="uppercase"
            >
              {isEditMode ? 'Edit Notification' : 'New Notification'}
            </Text>
            <Icon
              _hover={{ color: 'white' }}
              as={X}
              boxSize={4}
              color="whiteAlpha.600"
              cursor="pointer"
              transition="color 0.2s"
              onClick={handleClose}
            />
          </Flex>
        </ModalHeader>
        <ModalBody py={5}>
          <VStack alignItems="stretch" spacing={0}>
            <EventTypeSelect
              disabled={isEditMode}
              value={eventType}
              onChange={setEventType}
            />
            <NotificationTypeToggle
              value={notificationType}
              onChange={setNotificationType}
            />
            <RecipientsSelect value={recipients} onChange={setRecipients} />
            <RelationshipToggle
              value={hasAssignedPartner}
              onChange={setHasAssignedPartner}
            />
            <DelaySelect value={delayMinutes} onChange={setDelayMinutes} />
            <DataToShareSelect value={dataToShare} onChange={setDataToShare} />
            <MessageEditor
              value={messageContent}
              onChange={setMessageContent}
            />
            <Flex justifyContent="space-between" mt={6}>
              {isEditMode ? (
                <Button
                  colorScheme="red"
                  isLoading={deleting}
                  variant="ghost"
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              ) : (
                <div />
              )}
              <Button
                _hover={{ bg: 'cyan.400' }}
                bg="cyan.500"
                color="indigo.900"
                isDisabled={!isValid}
                isLoading={isLoading}
                onClick={handleSave}
              >
                Save
              </Button>
            </Flex>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
